import React, { useState, useEffect, useRef } from 'react';
import styled, { ThemeProvider, createGlobalStyle } from 'styled-components';
import GameScreen from './GameScreen';
import MuteToggle from './MuteToggle';
import ThemeToggleIcon from './ThemeToggleIcon';
import Tutorial from './Tutorial';
import Toast from './Toast';
import ConfirmDialog from './ConfirmDialog';
import { createGame, connectToSocket, sendRevealTile, sendCheckWord, sendSwapTiles } from '../utils/api';
import { useGameInteractions } from '../hooks/useGameInteractions';
import { RetroButton } from '../styles/RetroButton';
import { lightTheme, darkTheme, createSharedComponents, createFloatingLetterEffect } from '../styles/theme';
import { playWordFoundSound, playTileRevealSound, playSwapSound, playGameStartSound, playLobbyButtonClickSound } from '../utils/sounds';
import { analyticsEvents } from '../utils/analytics';

const themes = {
  light: lightTheme,
  dark: darkTheme,
};

// Create shared components
const { SharedGlobalStyle, SharedBackgroundContainer } = createSharedComponents(styled, createGlobalStyle);

// Custom styled lobby container that extends the shared background
const LobbyContainer = styled(SharedBackgroundContainer)`
    text-align: center;
    position: relative;

    h1 {
      font-size: 2rem; // Adjusted for better viewing
    }
    h1, h2 {
      text-shadow: 3px 3px 4px rgba(0,0,0,0.25);
    }

    @media (max-width: 500px) {
      padding: 1rem;
      h1 {
        font-size: 2rem;
      }
    }
`;

const LobbyLayout = styled.div`
  display: flex;
  justify-content: center;
  align-items: stretch; // Make cards equal height
  gap: 1.5rem;
  width: 100%;
  max-width: 800px;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: center;
    gap: 1rem;
    max-width: 400px;
    padding: 0 1rem;
    box-sizing: border-box;
  }
`;

const LobbyCard = styled.div`
  background: ${({ theme }) => theme.body === '#1A1A1A' 
    ? 'rgba(255,255,255,0.1)' 
    : 'rgba(255,255,255,0.15)'};
  backdrop-filter: blur(15px);
  padding: 1.5rem;
  border-radius: 20px;
  width: 100%;
  box-shadow: 0 8px 32px ${({ theme }) => theme.body === '#1A1A1A' 
    ? 'rgba(0,0,0,0.3)' 
    : 'rgba(0,0,0,0.1)'};
  border: 2px solid ${({ theme }) => theme.body === '#1A1A1A' 
    ? 'rgba(255,255,255,0.15)' 
    : 'rgba(255,255,255,0.2)'};
  display: flex;
  flex-direction: column;
  justify-content: space-between;

  p {
    font-size: 1rem;
    margin-bottom: 1.2rem;
  }

  @media (max-width: 768px) {
    padding: 1.2rem;
    p {
      font-size: 1rem;
      margin-bottom: 1rem;
    }
  }
`;

const StyledInput = styled.input`
  font-family: ${({ theme }) => theme.fontFamily};
  font-size: 1rem;
  padding: 0.8rem;
  width: 80%;
  max-width: 250px;
  border-radius: 8px;
  border: 3px solid ${({ theme }) => theme.controlsBorder};
  background-color: ${({ theme }) => theme.body};
  color: ${({ theme }) => theme.text};
  text-align: center;
  margin-bottom: 1rem;
  box-shadow: inset 0 2px 4px rgba(0,0,0,0.1);

  &::placeholder {
    color: ${({ theme }) => theme.text};
    opacity: 0.6;
  }

  @media (max-width: 500px) {
    font-size: 1rem;
    padding: 0.6rem;
  }
`;

const RoomCodeHeading = styled.h2`
  font-family: 'Courier New', 'Monaco', 'Menlo', monospace;
  font-size: 1.6rem;
  font-weight: bold;
  text-shadow: 2px 2px 3px rgba(0,0,0,0.3);
  letter-spacing: 0.1em;
  background-color: rgba(255,255,255,0.15);
  padding: 1rem 1.5rem;
  border-radius: 15px;
  border: 2px solid rgba(255,255,255,0.2);
  margin: 0.5rem 0;
  display: inline-block;
  backdrop-filter: blur(10px);
`;

// const InstructionText = styled.p`
//   font-size: 1rem;
//   margin: 1rem 0;
//   padding: 1rem;
//   background-color: rgba(255,255,255,0.1);
//   border-radius: 10px;
//   border: 1px solid rgba(255,255,255,0.15);
//   backdrop-filter: blur(5px);
//   text-shadow: 1px 1px 2px rgba(0,0,0,0.2);
  
//   @media (max-width: 768px) {
//     font-size: 0.9rem;
//     padding: 0.8rem;
//   }
// `;

const MultiplayerLobby = () => {
    const [gameState, setGameState] = useState(null);
    const [socket, setSocket] = useState(null);
    const [roomId, setRoomId] = useState('');
    const [inputRoomId, setInputRoomId] = useState('');
    const [theme, setTheme] = useState('light'); // Added for GameScreen
    const [playerId, setPlayerId] = useState(null); // To store this client's player ID
    const [animationState, setAnimationState] = useState({ lastFound: [], lastSwapped: [], lastRevealed: null, lastFoundBy: null });
    const [isProcessingMove, setIsProcessingMove] = useState(false); // Optimistic locking
    const [showTutorial, setShowTutorial] = useState(false);
    const [copied, setCopied] = useState(false);
    const [toastMessage, setToastMessage] = useState(null);
    const [showGameEndedDialog, setShowGameEndedDialog] = useState(false);
    const gameWinTracked = useRef(false);

    useEffect(() => {
        // Use shared floating letter effect
        const cleanup = createFloatingLetterEffect(5000, 5, 2500);
        return cleanup;
    }, []);
    
    // State for UI interactions that are NOT YET in the hook
    const [localSelectedTiles, setLocalSelectedTiles] = useState([]); // Temp for swap
    const [showConfirmDialog, setShowConfirmDialog] = useState(false);
    
    const {
        isCheckMode,
        selectedTiles,
        showConfirmCheck,
        handleTileClick: handleInteractionTileClick,
        handleToggleCheckMode,
        handleConfirmCheckWord,
        handleCancelCheckWord,
    } = useGameInteractions(
        gameState,
        null, // onStateChange is not needed here
        null, // onWordFound is not needed here
        (tiles) => sendCheckWord(socket, tiles),
        null // onSwap is not needed here
    );

    const handleCreateGame = async () => {
        playLobbyButtonClickSound();
        const data = await createGame();
        if (data && data.room_id) {
            setRoomId(data.room_id);
        }
    };

    const handleCopyRoomCode = async () => {
        if (!roomId) return;
        try {
            await navigator.clipboard.writeText(roomId);
            setCopied(true);
            setTimeout(() => setCopied(false), 1500);
        } catch (e) {
            // No-op on failure
        }
    };

    const handleJoinGame = () => {
        playLobbyButtonClickSound();
        if (inputRoomId) {
            setRoomId(inputRoomId.toUpperCase());
        }
    };

    const handleStartGame = () => {
        if (socket && socket.readyState === WebSocket.OPEN) {
            playGameStartSound(); // Moved from socket message
            socket.send(JSON.stringify({ type: 'start_game' }));
            // Track game start when button is clicked
            analyticsEvents.gameStart('multiplayer');
        }
    };
    
    const handleTileClick = (row, col) => {
        // Reset animations on any tile click
        if (animationState.lastFound.length > 0 || animationState.lastSwapped.length > 0 || animationState.lastRevealed) {
            setAnimationState({ lastFound: [], lastSwapped: [], lastRevealed: null, lastFoundBy: null });
        }

        if (gameState.turn !== playerId || isProcessingMove) return;

        // The hook now handles the tile reveal sound for check mode
        if (isCheckMode) {
            handleInteractionTileClick(row, col);
        } else {
            const tile = gameState.grid[row][col];
            const isRevealedByCurrentPlayer = tile.revealedBy && tile.revealedBy.includes(playerId);
            
            // If clicking a revealed tile, enter/continue swap mode
            if (isRevealedByCurrentPlayer) {
                handleSwapSelection(row, col);
            } else {
                // Otherwise, reveal the tile
                playTileRevealSound();
                setIsProcessingMove(true);
                sendRevealTile(socket, row, col, playerId);
            }
        }
    };

    // THIS FUNCTION IS NOW HANDLED BY THE HOOK AND CAN BE REMOVED
    // const handleCheckWord = () => {
    //     if (gameState.turn !== playerId) return;
    //     if (!isCheckMode) {
    //         setIsCheckMode(true);
    //         setIsSwapMode(false);
    //         setSelectedTiles([]);
    //     } else {
    //         if (selectedTiles.length >= 4) {
    //             sendCheckWord(socket, selectedTiles);
    //         }
    //         setIsCheckMode(false);
    //         setSelectedTiles([]);
    //     }
    // };

    // Removed legacy swap toggle; swap now initiates via double-click

    const handleSwapSelection = (row, col) => {
        // If the clicked tile is the one already selected, cancel swap selection
        if (localSelectedTiles.length === 1 && localSelectedTiles[0].row === row && localSelectedTiles[0].col === col) {
            setLocalSelectedTiles([]);
            return;
        }

        const newSelectedTiles = [...localSelectedTiles, { row, col }];
        setLocalSelectedTiles(newSelectedTiles);
        if (newSelectedTiles.length === 2) {
            setShowConfirmDialog(true);
        }
    };

    const handleConfirmSwap = () => {
        if (gameState.turn !== playerId) return;
        if (localSelectedTiles.length === 2) {
            sendSwapTiles(socket, localSelectedTiles[0], localSelectedTiles[1]);
        }
        setShowConfirmDialog(false);
        setLocalSelectedTiles([]);
    };

    const handleCancelSwap = () => {
        setShowConfirmDialog(false);
        setLocalSelectedTiles([]);
    };

    const toggleTheme = () => {
        const newTheme = theme === 'light' ? 'dark' : 'light';
        setTheme(newTheme);
        
        // Save theme preference to localStorage
        localStorage.setItem('gameTheme', newTheme);
        
        // Analytics tracking is handled by ThemeToggleIcon component
    };

    const handleShowTutorial = () => {
        setShowTutorial(true);
        analyticsEvents.helpButtonClicked('multiplayer');
    };

    const handleTutorialComplete = () => {
        setShowTutorial(false);
    };


    // Load theme preference from localStorage on component mount
    useEffect(() => {
        const savedTheme = localStorage.getItem('gameTheme');
        if (savedTheme && (savedTheme === 'light' || savedTheme === 'dark')) {
            setTheme(savedTheme);
        }
    }, []);

    const handlers = {
        handleTileClick,
        handleCheckWord: handleToggleCheckMode, // Use the new handler from the hook
        handleConfirmSwap,
        handleCancelSwap,
        handleDifficultyChange: () => {}, // Not used in multiplayer
        handleConfirmCheckWord,
        handleCancelCheckWord,
        handleChangePlayerName: (newName) => {
            if (!socket || socket.readyState !== WebSocket.OPEN) return;
            socket.send(JSON.stringify({ type: 'set_name', payload: { name: newName } }));
        },
    };

    // handles socket messages received from the server
    useEffect(() => {
        if (roomId) {

            
            const handleSocketMessage = (data) => {
                if (data.type === 'player_assigned') {
                    setPlayerId(data.payload.playerId);
                } else if (data.type === 'game_state') {
                    const newGameState = data.payload;
                    
                    // Check if this update includes word found metadata
                    if (data.metadata && (data.metadata.action === 'word_found' || (data.metadata.foundWords && data.metadata.foundWords.length > 0))) {
                        // Play word found sound
                        playWordFoundSound();
                        
                        // Handle letter replacement timing
                        const words = data.metadata.foundWords || [data.metadata];
                        const allTiles = words.flatMap(w => w.tiles);
                        
                        // Update game state but keep old grid for animation
                        setGameState(prevState => ({
                            ...newGameState,
                            grid: prevState?.grid || newGameState.grid, // Keep old grid for animation
                        }));
                        
                        // Start poof animation
                        setAnimationState(prevState => ({ ...prevState, lastFound: allTiles }));
                        
                        // Calculate animation duration and apply new grid after animation
                        const maxDelay = (allTiles.length - 1) * 0.15;
                        const animationDuration = 1000 + (maxDelay * 1000);
                        
                        setTimeout(() => {
                            // Update to new grid with replaced letters
                            setGameState(prevState => ({
                                ...prevState,
                                grid: newGameState.grid,
                            }));
                            setAnimationState(prevState => ({ ...prevState, lastFound: [] }));
                        }, animationDuration + 100);
                    } else {
                        // Normal update - no word found
                        setGameState(newGameState);
                        if (data.metadata) {
                            handleAnimationMetadata(data.metadata, false);
                            
                            // Handle player left notification
                            if (data.metadata.action === 'player_left') {
                                const playerName = data.metadata.player_name || data.metadata.player_id;
                                const message = data.metadata.was_their_turn
                                    ? `${playerName} left the game. Turn passed to next player.`
                                    : `${playerName} left the game.`;
                                setToastMessage(message);
                            }
                            
                            // Handle game ended due to insufficient players
                            if (data.metadata.action === 'game_ended' && data.metadata.reason === 'insufficient_players') {
                                // Update game state first so it shows ended
                                setGameState(newGameState);
                                // Then show dialog
                                setShowGameEndedDialog(true);
                                return; // Don't update game state again below
                            }
                        }
                    }
                    
                    setIsProcessingMove(false); // Unlock UI on state update
                } else if (data.type === 'error') {
                    alert(data.payload.message);
                    setRoomId('');
                } else if (data.type === 'invalid_action') {
                    // The backend rejected the move (e.g., wrong turn, invalid tile)
                    setIsProcessingMove(false); // Unlock the UI
                }
            };
            const newSocket = connectToSocket(roomId, handleSocketMessage);
            setSocket(newSocket);

            return () => {
                newSocket.close();
            };
        }
    }, [roomId]);

    // Track game win for multiplayer
    useEffect(() => {
        if (gameState && gameState.game_over && gameState.winner && !gameWinTracked.current) {
            gameWinTracked.current = true;
            const playerWords = gameState.found_words?.[playerId]?.length || 0;
            // Get opponent words (total words found by all other players)
            const allPlayerWords = Object.values(gameState.found_words || {}).reduce((sum, words) => sum + (words?.length || 0), 0);
            const opponentWords = allPlayerWords - playerWords;
            
            analyticsEvents.gameWin({
                mode: 'multiplayer',
                winner: gameState.winner,
                playerWords,
                opponentWords,
            });
        }
        // Reset tracking when game state resets
        if (!gameState || !gameState.game_over) {
            gameWinTracked.current = false;
        }
    }, [gameState, playerId]);

    // handles animation metadata received from the server
    const handleAnimationMetadata = (metadata, hasWordFound = false) => {
        if (metadata.action === 'word_found' || (metadata.foundWords && metadata.foundWords.length > 0)) {
            // Word found animations are now handled in the socket message handler above
            // This function now only handles other animations
            return;
        } else if (metadata.action === 'tiles_swapped') {
            // Only play swap sound if no word was found in this action
            // Word found celebration takes priority over swap sound
            if (!hasWordFound) {
                playSwapSound();
            }
            
            setAnimationState(prevState => ({ ...prevState, lastSwapped: metadata.tiles }));
        } else if (metadata.action === 'tile_revealed') {
            setAnimationState(prevState => ({ ...prevState, lastRevealed: metadata.tile }));
        }
    };

    const TopBar = styled.div`
        width: 100%;
        display: flex;
        align-items: center;
        justify-content: space-evenly;
        gap: 1rem;
        margin-bottom: 1rem;
        
        @media (max-width: 500px) {
            gap: 0.5rem;
        }
    `;

    const HelpButton = styled.button`
        background: rgba(255, 255, 255, 0.15);
        backdrop-filter: blur(15px);
        border: 2px solid rgba(255, 255, 255, 0.2);
        border-radius: 50%;
        width: 48px;
        height: 48px;
        display: flex;
        align-items: center;
        justify-content: center;
        cursor: pointer;
        font-size: 1.7rem;
        font-weight: bold;
        color: ${({ theme }) => theme.text};
        transition: all 0.3s ease;
        box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
        line-height: 1;
        box-sizing: border-box;
        margin: 0;

        &:hover {
            transform: scale(1.06);
            background: rgba(255, 255, 255, 0.25);
            box-shadow: 0 6px 20px rgba(0, 0, 0, 0.15);
        }

        &:active {
            transform: scale(0.96);
        }

        @media (max-width: 768px) {
            width: 40px;
            height: 40px;
            font-size: 1.2rem;
        }
    `;

    const renderLobby = () => {
        if (!gameState) {
            return (
              <LobbyLayout>
                  <LobbyCard>
                    <div>
                      {/* <h2>Create a New Game</h2> */}
                      <p>Start a new game and invite friends to join!</p>
                      <p style={{ fontSize: '0.9rem', opacity: 0.8, marginTop: '0.5rem' }}>
                        2-5 players can join your room
                      </p>
                    </div>
                    <div>
                      <RetroButton onClick={handleCreateGame}>Create Game</RetroButton>
                    </div>
                  </LobbyCard>

                  <LobbyCard>
                    <div>
                      {/* <h2>Join an Existing Game</h2> */}
                      <p>Enter a room code below to jump into the action.</p>
                    </div>
                    <div>
                      <StyledInput 
                        type="text" 
                        value={inputRoomId} 
                        onChange={(e) => setInputRoomId(e.target.value)}
                        placeholder="ENTER CODE" 
                        maxLength="5"
                      />
                      <RetroButton onClick={handleJoinGame}>Join Game</RetroButton>
                    </div>
                  </LobbyCard>
                </LobbyLayout>
            );
        }
      
      if (gameState.status === 'waiting') {
            return (
                <LobbyLayout>
                    <LobbyCard>
                        <RoomCodeHeading>
                          Room: {roomId}
                          <button
                            onClick={handleCopyRoomCode}
                            aria-label="Copy room code"
                            hoverColor="#FFFFFF"
                            title="Copy room code"
                            style={{
                              marginLeft: '0.5rem',
                              background: 'transparent',
                              border: 'none',
                              cursor: 'pointer',
                              display: 'inline-flex',
                              alignItems: 'center',
                              verticalAlign: 'top',
                              padding: 2,
                              color: '#FFFFFF'
                            }}
                          >
                            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <rect x="7" y="3" width="12" height="16" rx="2" stroke="currentColor" strokeWidth="2"/>
                              <rect x="3" y="7" width="12" height="16" rx="2" stroke="currentColor" strokeWidth="2"/>
                            </svg>
                          </button>
                          {copied && <span style={{ marginLeft: '0.5rem', fontSize: '0.9rem' }}>Copied!</span>}
                        </RoomCodeHeading>
                        <p>Waiting for players... ({gameState.players.length} / 5)</p>
                        <p style={{ fontSize: '0.9rem', opacity: 0.8, marginTop: '0.5rem' }}>
                            2-5 players can join. Minimum 2 players required to start.
                        </p>
                        {gameState.players.length >= 2 && (
                            <RetroButton onClick={handleStartGame}>Start Game</RetroButton>
                        )}
                    </LobbyCard>
                </LobbyLayout>
            );
        }
    };
    
    if (gameState && (gameState.status === 'active' || gameState.status === 'ended')) {
        // Determine which player's data to show
        let playerKeys = [];
        if (Array.isArray(gameState.players) && gameState.players.length > 0) {
            playerKeys = gameState.players.map(p => typeof p === 'string' ? p : (p.playerId || p.id || p));
        } else if (gameState.scores) {
            playerKeys = Object.keys(gameState.scores);
        } else if (gameState.found_words) {
            playerKeys = Object.keys(gameState.found_words);
        }
        const myPlayerKey = playerId || playerKeys[0]; // Fallback
        
        // Build all players data for display
        const allPlayersData = playerKeys.map(pKey => ({
            playerId: pKey,
            foundWords: (gameState.found_words && gameState.found_words[pKey]) ? gameState.found_words[pKey] : [],
            isCurrentPlayer: pKey === myPlayerKey,
        }));

        const screenGameState = {
            ...gameState,
            // playerScore: gameState.scores[myPlayerKey] || 0,  // REMOVED: No more scoring
            // aiScore: gameState.scores[opponentPlayerKey] || 0,  // REMOVED: No more scoring
            playerFoundWords: gameState.found_words[myPlayerKey] || [],
            aiFoundWords: gameState.found_words[playerKeys.find(p => p !== myPlayerKey)] || [], // Keep for backward compatibility
            allPlayersData: allPlayersData, // New: pass all players data
            playerTurn: gameState.turn === myPlayerKey, // Map turn string to boolean
            // selectedTiles are now managed differently
            selectedTiles: localSelectedTiles.length > 0 ? localSelectedTiles : selectedTiles,
            animationState: animationState,
            showConfirmDialog: showConfirmDialog,
            showConfirmCheck: showConfirmCheck, // Pass down the new state
            isCheckMode: isCheckMode, // Use state from the hook
            isProcessingMove: isProcessingMove, // Pass down the lock state
            gameOver: gameState.game_over,
            winner: gameState.winner,
            names: gameState.names || {},
            totalPlayers: playerKeys.length, // Pass total player count
        };

        return (
            <>
                <GameScreen
                    theme={theme}
                    toggleTheme={toggleTheme}
                    gameState={screenGameState}
                    handlers={handlers}
                    setAnimationState={setAnimationState}
                    playerId={myPlayerKey} // Pass the client's player ID
                    isMultiplayer={true}
                    showCheckButton={false}
                />
                {toastMessage && (
                    <Toast
                        message={toastMessage}
                        duration={3000}
                        onClose={() => setToastMessage(null)}
                        theme={themes[theme]}
                    />
                )}
                {showGameEndedDialog && (
                    <ConfirmDialog
                        message="Not enough players to continue. Returning to home."
                        onConfirm={() => {
                            setShowGameEndedDialog(false);
                            window.location.href = '/heist/';
                        }}
                        confirmText="OK"
                        showCancel={false}
                        theme={themes[theme]}
                    />
                )}
            </>
        );
    }

    return (
        <ThemeProvider theme={themes[theme]}>
            <>
                <SharedGlobalStyle theme={themes[theme]} />
                <LobbyContainer theme={themes[theme]} className="lobby-container">
                    <TopBar>
                        <div style={{ display: 'inline-flex', alignItems: 'center' }}>
                            <ThemeToggleIcon theme={theme} toggleTheme={toggleTheme} />
                        </div>
                        <div style={{ display: 'inline-flex', alignItems: 'center' }}>
                            <HelpButton theme={themes[theme]} onClick={handleShowTutorial}>?</HelpButton>
                        </div>
                        <div style={{ display: 'inline-flex', alignItems: 'center' }}>
                            <HelpButton as="a" href="/heist/" theme={themes[theme]} title="Back to Home">
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                    <polyline points="9,22 9,12 15,12 15,22" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                </svg>
                            </HelpButton>
                        </div>
                        <div style={{ display: 'inline-flex', alignItems: 'center' }}>
                            <MuteToggle />
                        </div>
                    </TopBar>
                    <h1>Multiplayer</h1>
                    {/* {!gameState && (
                        <InstructionText>
                            <strong>2-5 players</strong> can play together! Create a room and share the code with your friends.
                        </InstructionText>
                    )} */}
                    {renderLobby()}
                    {/* Help moved to TopBar */}
                </LobbyContainer>
                <Tutorial 
                  isVisible={showTutorial} 
                  onComplete={handleTutorialComplete} 
                  theme={themes[theme]}
                />
            </>
        </ThemeProvider>
    );
};

export default MultiplayerLobby; 